import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Images, X } from "lucide-react";

export default function GalleryCarousel({
  images = [],
  deleteMode = false,
  deletingId = null,
  onDelete,
  isAdmin = false,
  autoPlay = true,
  autoPlayMs = 2500,
}) {
  const safeImages = useMemo(
    () => (Array.isArray(images) ? images.filter(Boolean) : []),
    [images]
  );

  const [index, setIndex] = useState(0);
  const [viewerIndex, setViewerIndex] = useState(null);
  const timerRef = useRef(null);
  const hoveringRef = useRef(false);
  const touchStartRef = useRef(null);

  const imageUrl = (img) => img?.url || img;

  const go = useCallback((dir) => {
    if (!safeImages.length) return;
    setIndex((i) => {
      const n = safeImages.length;
      return (i + dir + n) % n;
    });
  }, [safeImages.length]);

  const openViewer = (nextIndex) => {
    setViewerIndex(nextIndex);
  };

  const closeViewer = useCallback(() => setViewerIndex(null), []);

  const goViewer = useCallback((dir) => {
    setViewerIndex((current) => {
      if (current === null || !safeImages.length) return current;
      return (current + dir + safeImages.length) % safeImages.length;
    });
  }, [safeImages.length]);

  // autoplay con pausa en hover
  useEffect(() => {
    if (!autoPlay || safeImages.length <= 1) return;
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      if (!hoveringRef.current) go(1);
    }, Math.max(900, autoPlayMs));
    return () => clearInterval(timerRef.current);
  }, [autoPlay, autoPlayMs, safeImages.length, go]);

  useEffect(() => {
    if (index > safeImages.length - 1) {
      setIndex(0);
    }
  }, [index, safeImages.length]);

  useEffect(() => {
    if (viewerIndex === null) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event) => {
      if (event.key === "Escape") closeViewer();
      if (event.key === "ArrowLeft") goViewer(-1);
      if (event.key === "ArrowRight") goViewer(1);
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [viewerIndex, closeViewer, goViewer]);

  if (!safeImages.length) return null;

  const previewImages = safeImages.slice(0, Math.min(3, safeImages.length));
  const activeViewerImage = viewerIndex !== null ? safeImages[viewerIndex] : null;

  return (
    <div className="relative mx-auto w-full max-w-5xl select-none">
      {/* Mobile: mosaico compacto, abre visor fullscreen */}
      <div className="md:hidden">
        <div className="grid grid-cols-2 gap-2">
          {previewImages.map((img, i) => {
            const url = imageUrl(img);
            const isHero = i === 0;
            const isLastPreview = i === previewImages.length - 1;

            return (
              <button
                type="button"
                key={img._id || url || i}
                onClick={() => openViewer(i)}
                className={`relative overflow-hidden bg-white/[0.04] border border-white/10 text-left active:scale-[0.99] transition ${isHero ? "col-span-2 aspect-[4/3] rounded-[28px]" : "aspect-[4/3] rounded-2xl"
                  }`}
                aria-label={`Abrir foto ${i + 1}`}
              >
                <img
                  src={url}
                  alt={`Foto ${i + 1}`}
                  className="h-full w-full object-cover"
                  draggable={false}
                />

                {safeImages.length > 3 && isLastPreview && (
                  <div className="absolute inset-0 bg-black/55 backdrop-blur-[2px] flex flex-col items-center justify-center gap-2">
                    <Images size={22} className="text-primary-orange" />
                    <span className="text-xs font-black uppercase tracking-[2px] text-white">
                      Ver todas
                    </span>
                    <span className="text-[10px] font-bold text-white/50">
                      {safeImages.length} fotos
                    </span>
                  </div>
                )}

                {deleteMode && isAdmin && (
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      onDelete?.(img._id);
                    }}
                    disabled={deletingId === img._id}
                    className="absolute top-3 right-3 rounded-full bg-black/70 p-2 text-red-300 border border-white/10"
                    title="Eliminar esta foto"
                  >
                    <TrashIcon />
                  </button>
                )}
              </button>
            );
          })}
        </div>

      </div>

      {/* marco + fondo letterbox + sombra */}
      <div
        className="relative hidden md:block overflow-hidden rounded-2xl bg-slate-900 shadow-xl ring-1 ring-black/5"
        onMouseEnter={() => (hoveringRef.current = true)}
        onMouseLeave={() => (hoveringRef.current = false)}
      >
        {/* pista */}
        <div
          className="flex transition-transform duration-500 ease-out"
          style={{ transform: `translateX(-${index * 100}%)` }}
        >
          {safeImages.map((img, i) => (
            <div className="w-full shrink-0" key={img._id || imageUrl(img) || i}>
              <div className="relative">
                {/* 
                  h-[]: alto base del carrusel
                  md:h-[520px] sube en pantallas medianas
                  object-contain: no recorta (si querés recorte/llenado --> object-cover)
                */}
                <img
                  src={imageUrl(img)}
                  alt={`Foto ${i + 1}`}
                  className="block w-full h-[580px] object-contain bg-background cursor-zoom-in"
                  onClick={() => openViewer(i)}
                  draggable={false}
                />

                {/* botón eliminar por imagen */}
                {deleteMode && isAdmin && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete?.(img._id);
                    }}
                    disabled={deletingId === img._id}
                    className="absolute top-3 right-3 rounded-full bg-white/90 p-2 text-red-600 shadow hover:bg-white transition"
                    title="Eliminar esta foto"
                  >
                    {/* trash icon */}
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      className="h-5 w-5"
                    >
                      <path d="M6 7.5h12M9.75 7.5v9.75m4.5-9.75v9.75M4.5 7.5l1.5 12A2.25 2.25 0 008.25 21h7.5a2.25 2.25 0 002.25-2.25l1.5-12M10.125 3h3.75c.62 0 1.125.505 1.125 1.125V6H9V4.125C9 3.505 9.505 3 10.125 3z" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* flechas */}
        {safeImages.length > 1 && (
          <>
            <button
              onClick={() => go(-1)}
              className="group absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center rounded-full bg-black/40 text-white backdrop-blur-md border border-white/10 hover:bg-primary-orange hover:border-transparent transition-all active:scale-90"
              aria-label="Anterior"
            >
              <ChevronLeft size={24} className="group-hover:scale-110 transition" />
            </button>
            <button
              onClick={() => go(1)}
              className="group absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center rounded-full bg-black/40 text-white backdrop-blur-md border border-white/10 hover:bg-primary-orange hover:border-transparent transition-all active:scale-90"
              aria-label="Siguiente"
            >
              <ChevronRight size={24} className="group-hover:scale-110 transition" />
            </button>
          </>
        )}
      </div>

      {/* dots */}
      {safeImages.length > 1 && (
        <div className="mt-6 hidden md:flex items-center justify-center gap-3">
          {safeImages.map((_, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              aria-label={`Ir a la foto ${i + 1}`}
              className={`h-2 rounded-full transition-all duration-300
                ${i === index ? "w-8 bg-primary-orange shadow-lg shadow-orange-500/20" : "w-2 bg-white/20 hover:bg-white/40"}`}
            />
          ))}
        </div>
      )}

      {activeViewerImage && (
        <div
          className="fixed inset-0 z-[110] bg-black/95 backdrop-blur-xl flex flex-col"
          onClick={closeViewer}
          onTouchStart={(event) => {
            touchStartRef.current = event.touches[0].clientX;
          }}
          onTouchEnd={(event) => {
            if (touchStartRef.current === null) return;
            const delta = event.changedTouches[0].clientX - touchStartRef.current;
            touchStartRef.current = null;
            if (Math.abs(delta) > 45) {
              goViewer(delta > 0 ? -1 : 1);
            }
          }}
        >
          <div className="relative z-10 flex h-16 shrink-0 items-center justify-between px-4 pt-[env(safe-area-inset-top)]">
            <span className="rounded-full bg-white/10 px-3 py-1 text-xs font-black text-white/70">
              {viewerIndex + 1} / {safeImages.length}
            </span>
            <button
              type="button"
              onClick={closeViewer}
              className="h-11 w-11 rounded-full bg-white/10 text-white flex items-center justify-center border border-white/10"
              aria-label="Cerrar galería"
            >
              <X size={22} />
            </button>
          </div>

          <div className="relative flex-1 min-h-0 px-3 pb-6 sm:px-8" onClick={(event) => event.stopPropagation()}>
            <img
              src={imageUrl(activeViewerImage)}
              alt={`Foto ${viewerIndex + 1}`}
              className="h-full w-full object-contain"
              draggable={false}
            />

            {safeImages.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={() => goViewer(-1)}
                  className="hidden sm:flex absolute left-6 top-1/2 -translate-y-1/2 h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white border border-white/10 hover:bg-primary-orange transition"
                  aria-label="Foto anterior"
                >
                  <ChevronLeft size={24} />
                </button>
                <button
                  type="button"
                  onClick={() => goViewer(1)}
                  className="hidden sm:flex absolute right-6 top-1/2 -translate-y-1/2 h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white border border-white/10 hover:bg-primary-orange transition"
                  aria-label="Foto siguiente"
                >
                  <ChevronRight size={24} />
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function TrashIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      className="h-4 w-4"
    >
      <path d="M6 7.5h12M9.75 7.5v9.75m4.5-9.75v9.75M4.5 7.5l1.5 12A2.25 2.25 0 008.25 21h7.5a2.25 2.25 0 002.25-2.25l1.5-12M10.125 3h3.75c.62 0 1.125.505 1.125 1.125V6H9V4.125C9 3.505 9.505 3 10.125 3z" />
    </svg>
  );
}
